import React, { useEffect, useState } from 'react';
import { Mail } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { oapi, odownload } from '@/lib/officialsApi';
import { PayFilters } from './PayFilters';
import { DownloadMenu, Loading, Money, Panel } from './PayBits';

const KINDS = [['summary', 'Payment summary'], ['outstanding', 'Outstanding'], ['game_cost', 'Cost per game'], ['league_expense', 'League expenses & budgets'], ['statement', 'Official statement']];

const Cell = ({ c, v, currency }) => (c.money ? <Money minor={v || 0} currency={currency} /> : <>{v ?? ''}</>);

const ReportSection = ({ s, currency, i }) => (
  <div className="overflow-x-auto" data-testid={`report-section-${i}`}>
    <div className="px-4 pt-4 pb-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500">{s.title}</div>
    <table className="w-full text-sm">
      <thead><tr className="text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 text-left">
        {s.columns.map(c => <th key={c.key} className={`px-3 py-2 whitespace-nowrap ${c.money ? 'text-right' : ''}`}>{c.label}</th>)}
      </tr></thead>
      <tbody>
        {s.rows.map((r, j) => (
          <tr key={j} className="border-t">{s.columns.map(c => <td key={c.key} className={`px-3 py-2 ${c.money ? 'text-right tabular-nums' : 'text-slate-700'}`}><Cell c={c} v={r[c.key]} currency={currency} /></td>)}</tr>
        ))}
        {s.totals && (
          <tr className="border-t-2 bg-slate-50 font-bold" data-testid={`report-totals-${i}`}>
            {s.columns.map(c => <td key={c.key} className={`px-3 py-2 ${c.money ? 'text-right' : ''}`}><Cell c={c} v={s.totals[c.key]} currency={currency} /></td>)}
          </tr>
        )}
      </tbody>
    </table>
    {!s.rows.length && <p className="py-6 text-center text-sm text-slate-500">No records for these filters.</p>}
  </div>
);

export const ReportsPanel = ({ orgId, currency, perms, competitions, officials, roles, tick }) => {
  const [kind, setKind] = useState('summary');
  const [f, setF] = useState({});
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState('');
  const needsOfficial = kind === 'statement' && !f.official_id;
  useEffect(() => {
    setRep(null);
    setErr('');
    if (needsOfficial) return;
    oapi(`/officials/payments/reports/${kind}`, { orgId, query: f }).then(setRep).catch(e => setErr(e.message));
  }, [orgId, kind, f, tick, needsOfficial]);
  const download = (format) => odownload(`/officials/payments/reports/${kind}`, { ...f, org_id: orgId, format }, `officials_${kind}.${format}`).catch(e => toast.error(e.message));
  const recipients = kind === 'statement' ? [f.official_id] : ['summary', 'outstanding'].includes(kind) && rep ? rep.sections[0].rows.map(r => r.official_id).filter(Boolean) : [];
  const email = () => oapi('/officials/payments/statements/send', { method: 'POST', orgId, body: { official_ids: recipients, from_date: f.from_date, to_date: f.to_date } })
    .then(r => toast.success(`Statement emailed to ${r.sent} of ${r.total} official${r.total === 1 ? '' : 's'}`)).catch(e => toast.error(e.message));
  return (
    <div className="space-y-4" data-testid="pay-reports">
      <div className="flex flex-wrap gap-1.5" data-testid="report-kinds">
        {KINDS.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setKind(k)} data-testid={`report-kind-${k}`}
            className={`px-3 py-1.5 rounded-full border text-sm font-semibold transition-colors ${kind === k ? 'bg-[#00247D] border-[#00247D] text-white' : 'bg-white text-slate-600 hover:border-slate-400'}`}>{l}</button>
        ))}
      </div>
      <PayFilters f={f} setF={setF} competitions={competitions} officials={officials} roles={roles} showStatus={false}
        showRole={['summary', 'game_cost', 'outstanding'].includes(kind)} officialLabel={kind === 'statement' ? 'Choose an official' : 'All officials'} />
      <Panel title={rep?.title || KINDS.find(k => k[0] === kind)[1]} desc={rep ? `${rep.subtitle} · ${rep.currency}` : ''} testId="report-panel"
        actions={<div className="flex flex-wrap gap-2">
          {perms.has('export') && recipients.length > 0 && <Button size="sm" variant="ghost" onClick={email} data-testid="report-email-statements"><Mail className="w-4 h-4 mr-1.5" />Email statement{recipients.length === 1 ? '' : 's'}</Button>}
          {perms.has('export') && !needsOfficial && <DownloadMenu onPick={download} testId="report-export" />}
        </div>}>
        {needsOfficial ? <p className="py-10 text-center text-sm text-slate-500" data-testid="report-need-official">Choose an official above to build their statement.</p>
          : err ? <p className="py-10 text-center text-sm text-red-600">{err}</p>
            : !rep ? <Loading /> : <div className="pb-2">{rep.sections.map((s, i) => <ReportSection key={s.title} s={s} currency={rep.currency || currency} i={i} />)}</div>}
      </Panel>
    </div>
  );
};
